import AvailablePackagesSection from '@/components/available-packages-section';
import BenefitsSection from '@/components/benefits-section';
import Brands from '@/components/brands';
import Faq from '@/components/faq';
import OurGuaranteesSection from '@/components/our-guarantees-section';
import ProductForm from '@/components/product-form';
import ReviewsCard from '@/components/reviews-card';
import SocialMediaCards from '@/components/social-media-cards';
import TestmonialCard from '@/components/testmonial-card';
import TrialSection from '@/components/trial-section';
import testmonials from '@/data/testmonials';
import MainLayout from '@/layouts/main-layout';
import { useI18n } from '@/lib/i18n';
import { OrderInteraction, OrderPackage, OrderPlatform } from '@/types';
import { CheckBadgeIcon, CursorArrowRippleIcon, FireIcon, PhotoIcon } from '@heroicons/react/24/outline';
import Fireworks from '@images/Fireworks.svg';
import Underline from '@images/Underline.svg';
import { usePage } from '@inertiajs/react';
import { useMemo } from 'react';

export default function Show() {
  const { t, formatNumber } = useI18n();
  const { props } = usePage<{ title: string; packages: OrderPackage[]; platform: OrderPlatform; interaction: OrderInteraction }>();
  const cheapestPackage = props.packages[0];
  const recommendedPackage = props.packages[4];
  const translationData = { platform: t(`packages.${props.platform}.title`), interaction: t(`packages.${props.interaction}`).toLowerCase() };
  const filteredTestmonials = useMemo(() => testmonials.filter((t) => t.interaction === props.interaction), []);

  return (
    <MainLayout absoluteHeader={true}>
      <section className="from-secondary/30 via-secondary/30 relative min-h-screen overflow-hidden bg-linear-to-b to-white px-6 pt-14 lg:px-8">
        <div className="relative z-10 mx-auto max-w-screen-lg items-start py-20 sm:py-32 md:flex lg:py-48">
          <main className={'relative z-10 md:w-6/12'}>
            <div className="space-y-4 text-center md:space-y-8 md:pt-24">
              <h2 className="text-4xl font-semibold tracking-tight text-balance text-black sm:text-6xl">
                {t('page.:platform :interaction from :price', {
                  platform: props.title,
                  interaction: cheapestPackage.title.toLowerCase(),
                  price: formatNumber(cheapestPackage.price, true),
                })}
              </h2>
              <p className="text-lg font-medium text-pretty text-black/80 sm:text-xl/8">
                {t(`packages.${props.platform}.descriptions.${props.interaction}`)}
              </p>
            </div>

            <div className="relative z-10 -mx-6 mt-6 flex justify-center gap-3 text-xs sm:text-sm">
              <div className={'flex flex-col items-center justify-center gap-1 sm:flex-row md:flex-col lg:flex-row'}>
                <CheckBadgeIcon className={'size-4 opacity-75'} />
                {t('page.checkmarks.0')}
              </div>
              <div className={'flex flex-col items-center justify-center gap-1 sm:flex-row md:flex-col lg:flex-row'}>
                <CheckBadgeIcon className={'size-4 opacity-75'} />
                {t('page.checkmarks.1')}
              </div>
              <div className={'flex flex-col items-center justify-center gap-1 sm:flex-row md:flex-col lg:flex-row'}>
                <CheckBadgeIcon className={'size-4 opacity-75'} />
                {t('page.checkmarks.2')}
              </div>
            </div>

            <div className={'mt-4 flex justify-center md:mt-8'}>
              <ReviewsCard />
            </div>
          </main>

          <aside className={'mt-8 md:mt-0 md:ml-8 md:w-6/12'}>
            <ProductForm
              packages={props.packages}
              platform={props.platform}
              interaction={props.interaction}
              cheapestPackage={cheapestPackage}
              recommendedPackage={recommendedPackage}
            />

            <Brands showText={true} className={'mt-4 justify-center'} />
          </aside>
        </div>
      </section>

      <section className="relative bg-white">
        <div className="mx-auto max-w-screen-lg p-6 md:py-12 lg:py-20">
          <h2 className="text-4xl font-semibold tracking-tight text-balance text-black sm:text-6xl">{t('page.How does it work?')}</h2>

          <div className={'mt-3 flex'}>
            <img src={Underline} alt={'Divider'} className={'pointer-events-none -mx-0.5 h-4 w-auto'} />
            <img src={Underline} alt={'Divider'} className={'pointer-events-none -mx-0.5 h-4 w-auto'} />
            <img src={Underline} alt={'Divider'} className={'pointer-events-none -mx-0.5 h-4 w-auto'} />
          </div>

          <div className="mt-8 max-w-prose space-y-4 text-lg font-medium text-pretty sm:text-xl">
            <p>{t('page.how_it_works_text.0')}</p>
            <p>{t('page.how_it_works_text.1', { platform: props.platform })}</p>
            <p>{t('page.how_it_works_text.2')}</p>
          </div>

          <div className="mt-20 space-y-6 lg:flex lg:space-y-0">
            <div className={'bg-secondary/10 self-stretch rounded-3xl p-6 lg:w-1/4'}>
              <CursorArrowRippleIcon className={'bg-secondary size-16 rounded-full p-3'} />
              <h3 className="mt-4 text-2xl font-semibold tracking-tight text-balance text-black">1. {t('page.Choose product')}</h3>
              <p className="text-md mt-2 font-medium text-pretty">{t('page.choose_product_text', translationData)}</p>
            </div>

            <div className={'flex flex-1 items-center justify-center'}>
              <div className="rotate-90 text-2xl font-semibold tracking-tight text-balance sm:text-3xl lg:rotate-0">&rarr;</div>
            </div>

            <div className={'bg-secondary/10 self-stretch rounded-3xl p-6 lg:w-1/4'}>
              <PhotoIcon className={'bg-secondary size-16 rounded-full p-3'} />
              <h3 className="mt-4 text-2xl font-semibold tracking-tight text-balance text-black">
                2. {props.interaction === OrderInteraction.Followers ? t('page.Choose account') : t('page.Choose post')}
              </h3>
              <p className="text-md mt-2 font-medium text-pretty">{t('page.choose_account_text', translationData)}</p>
            </div>

            <div className={'flex flex-1 items-center justify-center'}>
              <div className="rotate-90 text-2xl font-semibold tracking-tight text-balance sm:text-3xl lg:rotate-0">&rarr;</div>
            </div>

            <div className={'bg-secondary/10 self-stretch rounded-3xl p-6 lg:w-1/4'}>
              <FireIcon className={'bg-secondary size-16 rounded-full p-3'} />
              <h3 className="mt-4 text-2xl font-semibold tracking-tight text-balance text-black">3. {t('page.See results')}</h3>
              <p className="text-md mt-2 font-medium text-pretty">{t('page.see_results_text', translationData)}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative bg-white">
        <div className="mx-auto max-w-screen-lg p-6 md:py-12 lg:py-20">
          <h2 className="text-4xl font-semibold tracking-tight text-balance text-black sm:text-6xl">{t('page.You are in good company')}.</h2>

          <div className={'mt-3 flex'}>
            <img src={Underline} alt={'Divider'} className={'pointer-events-none -mx-0.5 h-4 w-auto'} />
            <img src={Underline} alt={'Divider'} className={'pointer-events-none -mx-0.5 h-4 w-auto'} />
            <img src={Underline} alt={'Divider'} className={'pointer-events-none -mx-0.5 h-4 w-auto'} />
          </div>

          <div className="mt-8 grid grid-cols-1 items-start gap-4 md:gap-8 lg:grid-cols-3">
            {filteredTestmonials.map((testmonial, n) => (
              <TestmonialCard key={n} testmonial={testmonial} />
            ))}
          </div>
        </div>
      </section>

      <TrialSection />

      <BenefitsSection />

      <OurGuaranteesSection />

      <AvailablePackagesSection packages={props.packages} />

      <div className={'relative mx-auto my-24 max-w-xs'}>
        <SocialMediaCards />

        <img
          src={Fireworks}
          alt={'Fireworks'}
          className={'pointer-events-none absolute top-0 right-0 h-32 w-auto -translate-y-20 -rotate-12 md:h-48 md:-translate-y-2/3'}
        />
      </div>

      <section className="mx-auto max-w-screen-lg">
        <div className="p-6 md:py-12 lg:py-20">
          <article className="prose lg:prose-lg mx-auto">
            <h1>{t('page.article.title', translationData)}</h1>
            <p>{t('page.article.intro', translationData)}</p>

            <h2>{t('page.article.why_product', translationData)}</h2>
            <p>{t('page.article.why_product_text', translationData)}</p>

            <h3>{t('page.article.how_product_help', translationData)}</h3>
            <ul>
              <li>{t('page.article.how_product_help_list.0', translationData)}</li>
              <li>{t('page.article.how_product_help_list.1', translationData)}</li>
              <li>{t('page.article.how_product_help_list.2', translationData)}</li>
            </ul>

            <h2>{t('page.article.why_choose_swedish', translationData)}</h2>
            <p>{t('page.article.why_choose_swedish_text', translationData)}</p>
            <ul>
              <li>{t('page.article.why_choose_swedish_list.0', translationData)}</li>
              <li>{t('page.article.why_choose_swedish_list.1', translationData)}</li>
              <li>{t('page.article.why_choose_swedish_list.2', translationData)}</li>
            </ul>

            <h2>{t('page.article.how_product_help_businesses', translationData)}</h2>
            <p>{t('page.article.how_product_help_businesses_text', translationData)}</p>
            <ul>
              <li>{t('page.article.how_product_help_businesses_list.0', translationData)}</li>
              <li>{t('page.article.how_product_help_businesses_list.1', translationData)}</li>
              <li>{t('page.article.how_product_help_businesses_list.2', translationData)}</li>
            </ul>

            <h2>{t('page.article.how_product_help_individuals', translationData)}</h2>
            <p>{t('page.article.how_product_help_individuals_text', translationData)}</p>
            <ul>
              <li>{t('page.article.how_product_help_individuals_list.0', translationData)}</li>
              <li>{t('page.article.how_product_help_individuals_list.1', translationData)}</li>
              <li>{t('page.article.how_product_help_individuals_list.2', translationData)}</li>
            </ul>

            <h2>{t('page.article.buying_product_standard', translationData)}</h2>
            <p>{t('page.article.buying_product_standard_text', translationData)}</p>

            <h2>{t('page.article.why_boostbuddy', translationData)}</h2>
            <ul>
              <li>{t('page.article.why_boostbuddy_list.0', translationData)}</li>
              <li>{t('page.article.why_boostbuddy_list.1', translationData)}</li>
              <li>{t('page.article.why_boostbuddy_list.2', translationData)}</li>
              <li>{t('page.article.why_boostbuddy_list.3', translationData)}</li>
            </ul>

            <h2>{t('Frequently Asked Questions')}</h2>
            <Faq platform={props.platform} interaction={props.interaction} />
          </article>
        </div>
      </section>
    </MainLayout>
  );
}
